import { useMemo } from "react";
import ProjectStageListItem from "./ProjectStageListItem";
import ProjectTeamSection from "./ProjectTeamSection";
import { getCurrentStageIds } from "../../utils/currentStage";

export default function ProjectLeftPanel({
  project,
  selectedStageId,
  onSelectStage,
  onAllocate,
  onRemoveAllocation,
  loading,
  isAdmin,
  width,
}) {
  const stages = useMemo(() => {
    const raw = project?.workflow?.definition?.stages || [];
    const connections = project?.workflow?.definition?.connections || [];

    const inDegree = new Map(raw.map((s) => [s.id, 0]));
    const adj = new Map(raw.map((s) => [s.id, []]));
    connections.forEach(({ sourceStageId, targetStageId }) => {
      if (adj.has(sourceStageId)) adj.get(sourceStageId).push(targetStageId);
      if (inDegree.has(targetStageId)) inDegree.set(targetStageId, inDegree.get(targetStageId) + 1);
    });

    const queue = raw.filter((s) => inDegree.get(s.id) === 0);
    const sorted = [];
    while (queue.length) {
      const node = queue.shift();
      sorted.push(node);
      (adj.get(node.id) || []).forEach((targetId) => {
        const next = inDegree.get(targetId) - 1;
        inDegree.set(targetId, next);
        if (next === 0) queue.push(raw.find((s) => s.id === targetId));
      });
    }

    return sorted.length === raw.length ? sorted : raw;
  }, [project?.workflow?.definition?.stages, project?.workflow?.definition?.connections]);

  const graphByStageId = useMemo(() => {
    const map = new Map();
    (project?.stageGraph || []).forEach((entry) => map.set(entry.stageId, entry));
    return map;
  }, [project?.stageGraph]);

  const stageMetaMap = useMemo(() => {
    const map = new Map();
    const assignments = project?.stageAssignments || [];

    stages.forEach((stage) => {
      const stageAssignments = assignments
        .filter((assignment) => assignment.stageId === stage.id)
        .sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt));

      const activeAssignments = stageAssignments.filter((assignment) =>
        ["PENDING", "IN_PROGRESS", "SUBMITTED"].includes(assignment.status),
      );
      const latest = stageAssignments[0] || null;
      // Every concurrent assignee, not just a representative — a stage with
      // maxConcurrentAssignees > 1 has several people on it at once.
      const source = activeAssignments.length ? activeAssignments : latest ? [latest] : [];
      const assigneeNames = source
        .map((assignment) =>
          assignment.assignee
            ? `${assignment.assignee.firstName} ${assignment.assignee.lastName}`.trim()
            : "",
        )
        .filter(Boolean);

      let status = "PENDING";
      if (activeAssignments[0]) status = activeAssignments[0].status;
      else if (latest?.status === "DONE") status = "DONE";
      else if (latest?.status === "SKIPPED") status = "SKIPPED";
      else if (latest?.status === "RETURNED") status = "RETURNED";

      map.set(stage.id, { status, assigneeNames });
    });

    return map;
  }, [stages, project?.stageAssignments]);

  // Parallel fan-outs become indented groups so concurrent branches read as
  // concurrent instead of as a sequence.
  const parallelGroupBySource = useMemo(() => {
    const map = new Map();
    (project?.parallelGroups || []).forEach((group) =>
      map.set(group.sourceStageId, group.memberStageIds),
    );
    return map;
  }, [project?.parallelGroups]);

  const currentStageIds = useMemo(
    () => getCurrentStageIds(project),
    [project],
  );

  const renderStage = (stage) => {
    const meta = stageMetaMap.get(stage.id) || {};
    const graph = graphByStageId.get(stage.id) || {};
    return (
      <ProjectStageListItem
        key={stage.id}
        stage={stage}
        status={meta.status || "PENDING"}
        assigneeNames={meta.assigneeNames || []}
        isSelected={stage.id === selectedStageId}
        isCurrent={currentStageIds.includes(stage.id)}
        graphState={graph.state}
        waitingOn={graph.waitingOn || []}
        needsAssignee={!!graph.needsAssignee}
        isJoin={!!graph.isJoin}
        onClick={() => onSelectStage(stage.id)}
      />
    );
  };

  // Walk the topological order, pulling each parallel fan-out's members up
  // beneath their source so siblings always render together.
  const renderedTree = (() => {
    const stageById = new Map(stages.map((stage) => [stage.id, stage]));
    const emitted = new Set();
    const nodes = [];

    const emit = (stage, depth) => {
      if (!stage || emitted.has(stage.id)) return null;
      emitted.add(stage.id);

      const memberIds = parallelGroupBySource.get(stage.id) || [];
      const members = memberIds
        .map((id) => stageById.get(id))
        .filter((member) => member && !emitted.has(member.id));

      if (!members.length) {
        return (
          <div key={stage.id} className={`stage-node stage-node--depth-${depth}`}>
            {renderStage(stage)}
          </div>
        );
      }

      return (
        <div key={stage.id} className={`stage-node stage-node--depth-${depth}`}>
          {renderStage(stage)}
          <div className="stage-parallel-group">
            <span className="stage-parallel-label">
              Parallel — {members.length} branches run together
            </span>
            {members.map((member) => emit(member, depth + 1))}
          </div>
        </div>
      );
    };

    stages.forEach((stage) => {
      const node = emit(stage, 0);
      if (node) nodes.push(node);
    });

    return nodes;
  })();

  return (
    <div className="project-left-panel" style={width ? { width, minWidth: width } : undefined}>
      <div className="left-panel-section">
        <span className="left-panel-label">Workflow Stages</span>
        <div className="stage-list">{renderedTree}</div>
      </div>

      <ProjectTeamSection
        project={project}
        onAllocate={onAllocate}
        onRemoveAllocation={onRemoveAllocation}
        loading={loading}
        isAdmin={isAdmin}
      />
    </div>
  );
}
